// Shared bits of the ERP Sync screen (Phase 6, contract §6.7–§6.11): header with the tab sub-nav, the feed-mode strip,
// polling, and small formatters.

import { useEffect, useRef, type ReactNode } from 'react';
import type { DigestSummary, FeedState, Json, ResyncScope } from '../../api.ts';
import { Icon } from '../../components/Icon.tsx';
import { ERP_SYNC_TABS, href, type ErpSyncTab } from '../../router.ts';
import { fmtLocal, fmtTime, OPERATOR_TZ } from '../../time.ts';

const TAB_LABEL: Record<ErpSyncTab, string> = {
  overview: 'Overview',
  cutover: 'Cutover',
  inbox: 'Inbox',
  deliveries: 'Deliveries',
  digests: 'Digests',
};

export function ErpNav({ current }: { current: ErpSyncTab }) {
  return (
    <nav className="subnav" aria-label="ERP Sync">
      {ERP_SYNC_TABS.map((t) => (
        <a key={t} href={href.erpSync(t)} className={current === t ? 'on' : ''} aria-current={current === t ? 'page' : undefined}>
          {TAB_LABEL[t]}
        </a>
      ))}
    </nav>
  );
}

/** Page header: title, one-line explanation, optional actions, and the tab sub-nav on the right. */
export function ErpHeader({ tab, sub, actions }: { tab: ErpSyncTab; sub: ReactNode; actions?: ReactNode }) {
  return (
    <div className="page-h">
      <div>
        <h2>ERP sync</h2>
        <p>{sub}</p>
      </div>
      <span className="sp" />
      {actions}
      <ErpNav current={tab} />
    </div>
  );
}

const MODES: readonly { key: FeedState['mode']; label: string }[] = [
  { key: 'off', label: 'Off' },
  { key: 'shadow', label: 'Shadow' },
  { key: 'live', label: 'Live' },
];

const MODE_TEXT: Record<FeedState['mode'], string> = {
  off: 'No ERP feed: master data comes from import files.',
  shadow: 'The ERP feed fills a shadow copy; operators still use the imported data.',
  live: 'The ERP feed is the live replica; imports are off and SOC → ERP delivery is on.',
};

/** Off · Shadow · Live, with the dates that matter for the current mode. */
export function ModeStrip({ feed, rollbackAvailable, link }: { feed: FeedState; rollbackAvailable?: boolean; link?: boolean }) {
  return (
    <div className="erp-mode">
      <span className="lbl">Feed</span>
      <span className="erp-modes" role="img" aria-label={`Feed mode: ${feed.mode}`}>
        {MODES.map((m) => (
          <span key={m.key} className={feed.mode === m.key ? `on m-${m.key}` : ''}>
            {m.label}
          </span>
        ))}
      </span>
      <span className="note">{MODE_TEXT[feed.mode]}</span>
      {feed.mode === 'shadow' && feed.shadowSince && <span className="note">Shadow since <span className="mono">{fmtLocal(feed.shadowSince)}</span></span>}
      {feed.mode === 'live' && feed.promotedAt && <span className="note">Promoted <span className="mono">{fmtLocal(feed.promotedAt)}</span></span>}
      {feed.mode === 'live' && feed.rollbackUntil && (
        <span className="note">
          {rollbackAvailable === false ? 'Rollback window' : 'Rollback available until'} <span className="mono">{fmtLocal(feed.rollbackUntil)}</span>
        </span>
      )}
      {link && (
        <>
          <span className="sp" />
          <a href={href.erpSync('cutover')} className="small">
            Cutover controls
          </a>
        </>
      )}
    </div>
  );
}

/** Calls fn every `ms` while the page is visible, and once when it becomes visible again. */
export function usePoll(fn: () => void, ms: number): void {
  const ref = useRef(fn);
  useEffect(() => {
    ref.current = fn;
  });
  useEffect(() => {
    const visible = () => document.visibilityState === 'visible';
    const t = setInterval(() => {
      if (visible()) ref.current();
    }, ms);
    const onVis = () => {
      if (visible()) ref.current();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(t);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [ms]);
}

export const num = (n: number | null | undefined): string => (n === null || n === undefined ? '—' : n.toLocaleString('en-US'));

export function plural(n: number, one: string, many = `${one}s`): string {
  return `${num(n)} ${n === 1 ? one : many}`;
}

/** 0 → "0 s", 125 → "2 min", 7300 → "2 h". */
export function fmtAgeS(s: number | null | undefined): string {
  if (!s || s < 0) return '0 s';
  if (s < 60) return `${s} s`;
  if (s < 3600) return `${Math.round(s / 60)} min`;
  if (s < 86400) return `${Math.round(s / 3600)} h`;
  return `${Math.round(s / 86400)} d`;
}

export function clip(v: string | null | undefined, n = 80): string {
  if (!v) return '';
  return v.length > n ? `${v.slice(0, n - 1)}…` : v;
}

/** A diff value as one short line. */
export function valText(v: Json | undefined): string {
  if (v === null || v === undefined) return '∅';
  if (typeof v === 'string') return JSON.stringify(v);
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}

export function scopeText(scope: ResyncScope | null | undefined): string {
  if (!scope) return '—';
  if (scope.kind === 'all') return 'All';
  if (scope.kind === 'site') return `Site ${scope.site_id}`;
  return plural(scope.entities?.length ?? 0, 'entity', 'entities');
}

export function human(s: string | null | undefined): string {
  if (!s) return '—';
  const t = s.replace(/_/g, ' ').trim();
  return t.charAt(0).toUpperCase() + t.slice(1);
}

/** "03:00 · OK" and "2,914 entities hashed, 0 diffs" for a compared digest. */
export function digestHead(at: string | null | undefined, s: DigestSummary | null | undefined): { head: string; tail: string; ok: boolean } {
  const time = at ? fmtTime(at, OPERATOR_TZ, false) : '—';
  if (!s) return { head: `${time} · waiting`, tail: 'Not compared yet', ok: true };
  const ok = s.diffs === 0;
  return {
    head: `${time} · ${ok ? 'OK' : plural(s.diffs, 'diff')}`,
    tail: `${num(s.entities)} entities hashed, ${plural(s.diffs, 'diff')}${s.held ? `, ${num(s.held)} held` : ''}${s.partial ? ' · partial' : ''}`,
    ok,
  };
}

/** Result pill class for an inbox status (prototype .res ok / skip / wait / bad). */
export function inboxRes(status: string): 'ok' | 'skip' | 'wait' | 'bad' {
  if (status === 'applied') return 'ok';
  if (status.startsWith('skipped')) return 'skip';
  if (status === 'dead' || status === 'failed') return 'bad';
  return 'wait';
}

export function deliveryRes(status: string): 'ok' | 'skip' | 'wait' | 'bad' {
  if (status === 'sent') return 'ok';
  if (status === 'dead') return 'bad';
  if (status === 'discarded') return 'skip';
  return 'wait';
}

export function RefreshBtn({ onClick, busy }: { onClick: () => void; busy?: boolean }) {
  return (
    <button type="button" className="btn sm" onClick={onClick} disabled={busy}>
      <Icon name="sync" />
      Refresh
    </button>
  );
}
